'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Time } from '@morpho-org/morpho-ts';
import type { Address } from 'viem';
import { skipToken, useQuery } from '@tanstack/react-query';
import { toHistoryRange } from '@/helpers/timeseries';
import { orpc } from '@/lib/orpc/client';
interface UseDashboardUserHistoryParams {
  address?: Address;
  chainIds: ChainId[];
  timeFrame: Time.PeriodLike | null;
  enabled?: boolean;
}
interface UseDashboardMarketHistoryParams extends UseDashboardUserHistoryParams {
  type: 'loan' | 'collateral';
}
interface DashboardUserHistoryResult {
  points: {
    x: number;
    y: number | null;
  }[];
  loading: boolean;
  isError: boolean;
}
const STALE_TIME_MS = 60 * 1000;

/**
 * A `partial` series had a malformed point dropped by the bridge; the chart shows its error state
 * rather than a series with a hole in it, so one dropped point hides the chart. A series with
 * points but no USD value at all shows the error state too, rather than an empty or flat $0 chart.
 */
function toChartHistory(data: {
  points: {
    timestamp: number;
    valueUsd: number | null;
  }[];
  partial: boolean;
} | undefined, isLoading: boolean, isError: boolean): DashboardUserHistoryResult {
  return {
    points: data?.points.map(({
      timestamp,
      valueUsd
    }) => ({
      x: timestamp,
      y: valueUsd
    })) ?? [],
    loading: isLoading,
    isError: isError || data?.partial === true || !!data?.points.length && data.points.every(({
      valueUsd
    }) => valueUsd === null)
  };
}
export const useDashboardVaultHistory = t0 => {
  const $ = _c(9);
  const {
    address,
    chainIds,
    timeFrame,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== address || $[1] !== chainIds || $[2] !== enabled || $[3] !== timeFrame) {
    t2 = orpc.user.vaultPositionHistory.queryOptions({
      input: enabled && address ? {
        userAddress: address,
        chainIds,
        range: toHistoryRange(timeFrame)
      } : skipToken,
      staleTime: STALE_TIME_MS
    });
    $[0] = address;
    $[1] = chainIds;
    $[2] = enabled;
    $[3] = timeFrame;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data,
    isLoading,
    isError
  } = useQuery(t2);
  let t3;
  if ($[5] !== data || $[6] !== isError || $[7] !== isLoading) {
    t3 = toChartHistory(data, isLoading, isError);
    $[5] = data;
    $[6] = isError;
    $[7] = isLoading;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export const useDashboardMarketHistory = t0 => {
  const $ = _c(10);
  const {
    address,
    chainIds,
    timeFrame,
    type,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== address || $[1] !== chainIds || $[2] !== enabled || $[3] !== timeFrame || $[4] !== type) {
    t2 = orpc.user.marketPositionHistory.queryOptions({
      input: enabled && address ? {
        userAddress: address,
        chainIds,
        range: toHistoryRange(timeFrame),
        type
      } : skipToken,
      staleTime: STALE_TIME_MS
    });
    $[0] = address;
    $[1] = chainIds;
    $[2] = enabled;
    $[3] = timeFrame;
    $[4] = type;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    isLoading,
    isError
  } = useQuery(t2);
  let t3;
  if ($[6] !== data || $[7] !== isError || $[8] !== isLoading) {
    t3 = toChartHistory(data, isLoading, isError);
    $[6] = data;
    $[7] = isError;
    $[8] = isLoading;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};